import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DigitalFlyer, digitalFlyersApi } from "src/services/digitalFlyersApi";
import { keys } from "./keys";

/**
 * Digital flyers.
 *
 * The merchant-facing page previously used three different strategies for
 * refreshing after a write — prepend the new flyer on create, map-replace on
 * edit, full refetch on send — plus a manual `dispatch(getMerchant(...))` to pick
 * up the changed `freeFlyersRemaining`. All of that becomes one rule: invalidate
 * the flyer list, and invalidate the location record because the free-flyer
 * allowance lives on it.
 */
export const useMerchantFlyers = (merchantId: number | undefined) =>
  useQuery<DigitalFlyer[]>({
    queryKey: keys.flyers.byMerchant(merchantId ?? -1),
    enabled: !!merchantId && merchantId > 0,
    queryFn: () => digitalFlyersApi.getFlyersByMerchant(merchantId!),
  });

/** Admin queue, filtered by status. */
export const useAllFlyers = (status?: DigitalFlyer["status"]) =>
  useQuery<DigitalFlyer[]>({
    queryKey: keys.flyers.byStatus(status),
    queryFn: () => digitalFlyersApi.getAllFlyers(status),
  });

/**
 * Shared invalidation for every flyer write. `locationId` is optional because
 * the admin queue acts on flyers belonging to other merchants and has no
 * location record on screen to refresh.
 */
const useFlyerMutation = <TArgs extends { locationId?: number }, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.flyers.all });
      if (variables.locationId) {
        queryClient.invalidateQueries({
          queryKey: keys.location.detail(variables.locationId),
        });
      }
    },
  });
};

export const useCreateFlyer = () =>
  useFlyerMutation(
    async (args: {
      locationId: number;
      userId: number;
      message: string;
      cost: number;
      isFree?: boolean;
    }) =>
      digitalFlyersApi.createFlyer(
        args.locationId,
        args.userId,
        args.message,
        args.cost,
        args.isFree
      )
  );

export const useEditFlyer = () =>
  useFlyerMutation(
    async (args: { flyerId: number; message: string; locationId?: number }) =>
      digitalFlyersApi.editFlyer(args.flyerId, args.message)
  );

export const useSendFlyer = () =>
  useFlyerMutation(async (args: { flyerId: number; locationId?: number }) =>
    digitalFlyersApi.sendFlyer(args.flyerId)
  );

export const useDeleteFlyer = () =>
  useFlyerMutation(async (args: { flyerId: number; locationId?: number }) =>
    digitalFlyersApi.deleteFlyer(args.flyerId)
  );

export const useApproveFlyer = () =>
  useFlyerMutation(async (args: { flyerId: number; locationId?: number }) =>
    digitalFlyersApi.approveFlyer(args.flyerId)
  );

export const useRejectFlyer = () =>
  useFlyerMutation(
    async (args: {
      flyerId: number;
      rejectionMessage: string;
      locationId?: number;
    }) => digitalFlyersApi.rejectFlyer(args.flyerId, args.rejectionMessage)
  );
